import React from 'react';
import {
  Box,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { useCategoryContext } from '../contexts/CategoryContext';
import useNoteCategories from '../hooks/data/useNoteCategories';
import useNotes from '../hooks/data/useNotes';
import { stripHtml, NOTES_PREVIEW_LENGTH } from '../utils/bookmarkUtils';
import { Note } from '../types/notes';

interface NotesListBodyProps {
  /** Create a note in the current category. Wired by the layout (task 15). */
  onAddNote?: () => void;
}

function snippet(note: Note): string {
  const text = stripHtml(note.note_content).trim();
  if (text.length <= NOTES_PREVIEW_LENGTH) return text;
  return text.slice(0, NOTES_PREVIEW_LENGTH).trimEnd() + '…';
}

function formatModified(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Body view for a selected note category: child folders + a document list of the
 * notes in this category. Selecting a note opens it in the editor.
 */
const NotesListBody: React.FC<NotesListBodyProps> = ({ onAddNote }) => {
  const { selectedNoteCategory, selectNoteCategory, selectNote } =
    useCategoryContext();
  const categoryId = selectedNoteCategory?.category_id ?? null;

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useNoteCategories(categoryId);

  const {
    data: notes,
    loading: notesLoading,
    error: notesError,
  } = useNotes(categoryId ?? undefined);

  const loading = categoriesLoading || notesLoading;
  const error = categoriesError || notesError;

  if (loading) {
    return (
      <Box sx={{ px: 2, pt: 1, flex: 1 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 2, pt: 2, flex: 1 }}>
        <Typography color="error">
          {error.message || 'An error occurred while loading notes.'}
        </Typography>
      </Box>
    );
  }

  const hasCategories = categories.length > 0;
  const hasNotes = !!notes && notes.length > 0;

  return (
    <Box sx={{ px: 2, py: 1, flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 1,
        }}
      >
        <Button
          size="small"
          startIcon={<NoteAddIcon />}
          onClick={onAddNote}
          disabled={!onAddNote}
        >
          Add Note
        </Button>
      </Box>

      {!hasCategories && !hasNotes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {selectedNoteCategory
            ? 'This category is empty. Add a note to get started.'
            : 'No note categories yet.'}
        </Typography>
      )}

      {/* Child categories as folders */}
      {hasCategories && (
        <List disablePadding>
          {categories.map((category) => (
            <ListItemButton
              key={category.category_id}
              onClick={() => selectNoteCategory(category)}
              sx={{ minHeight: 48 }}
            >
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary={category.category_name} />
            </ListItemButton>
          ))}
        </List>
      )}

      {hasCategories && hasNotes && <Divider sx={{ my: 1 }} />}

      {/* Notes as a document list */}
      {hasNotes && (
        <List disablePadding>
          {notes!.map((note) => (
            <ListItemButton
              key={note.note_id}
              onClick={() => selectNote(note)}
              alignItems="flex-start"
              sx={{ minHeight: 56 }}
            >
              <ListItemIcon sx={{ mt: 0.5 }}>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText
                primary={note.note_title}
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {formatModified(note.last_modified)}
                    </Typography>
                    {snippet(note) && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                        noWrap
                      >
                        {snippet(note)}
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export default NotesListBody;
